import { HTMLAttributes, FC, useRef, useEffect } from "react";
import clsx from "classnames";
import confetti from "canvas-confetti";
import { Typography } from "../../core/Typography/Typography";
import { TypographyVariant } from "../../../constants/typography";
import { StaticImage } from "../../core/StaticImage/StaticImage";
import Button from "../../core/Button/Button";
import { Icon } from "../../core/Icon/Icon";
import Gift from "../../../assets/welcome/gift.webp";
import Gift2x from "../../../assets/welcome/gift@2x.webp";
import Gift3x from "../../../assets/welcome/gift@3x.webp";
import { isTV } from "../../../utils/platform";
import { useAppSelector } from "../../hooks";
import { selectIsDesktopView } from "../../../store/slices/appState";
import { useStyles } from "./FreeMonthReward.style";

interface IFreeMonthRewardProps {
  onClose: () => void;
}

export const FreeMonthReward: FC<
  IFreeMonthRewardProps & HTMLAttributes<HTMLDivElement>
> = (props) => {
  const classes = useStyles();
  const { className, onClose, ...othersProps } = props;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDesktop = useAppSelector(selectIsDesktopView);

  useEffect(() => {
    if (!canvasRef.current) return;
    const myConfetti = confetti.create(canvasRef.current, {
      resize: true,
      useWorker: true,
    });
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = {
      startVelocity: 30,
      spread: 360,
      ticks: 60,
      zIndex: 0,
      colors: ["#FFD700", "#FFC700", "#FFB700", "#FFAA00", "#FF9500"],
    };
    function randomInRange(min: number, max: number) {
      return Math.random() * (max - min) + min;
    }
    const interval = setInterval(function () {
      const timeLeft = animationEnd - Date.now();
      if (timeLeft <= 0) {
        return clearInterval(interval);
      }
      const particleCount = 50 * (timeLeft / duration);
      myConfetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
      });
      myConfetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
      });
    }, 250);

    return () => {
      clearInterval(interval);
    };
  }, []);

  return (
    <div {...othersProps} className={clsx(classes.root, className)}>
      <canvas ref={canvasRef} className={classes.canvasAnimate} />
      <Typography
        variant={
          isDesktop
            ? TypographyVariant.HeadingH4Bold
            : TypographyVariant.HeadingH6Bold
        }
      >
        ONE MORE THING
      </Typography>
      <Typography
        variant={
          isDesktop
            ? TypographyVariant.HeadingH1Bold
            : TypographyVariant.HeadingH3Bold
        }
        className={classes.title}
      >
        A Welcome Gift On Us
      </Typography>
      <div className={clsx(classes.box, !isTV && classes.boxGradient)}>
        <StaticImage
          src={Gift}
          src2x={Gift2x}
          src3x={Gift3x}
          alt="gift"
          className={classes.gift}
        />
        <Typography
          variant={TypographyVariant.HeadingH2Bold}
          className={classes.date}
        >
          +1 month
        </Typography>
        <Typography
          variant={TypographyVariant.BodyB1Regular}
          className={classes.access}
        >
          added to your included access
        </Typography>
      </div>
      <Typography
        variant={
          isDesktop
            ? TypographyVariant.BodyB3Medium
            : TypographyVariant.BodyB5Medium
        }
        className={classes.desc}
      >
        Thanks for being among the first to play.
      </Typography>
      <Button
        className={clsx(classes.goBtn, !isTV && "linearBg")}
        fullWidth={!isDesktop}
        onClick={onClose}
      >
        <Icon name="icon-bold-play" /> Start Playing
      </Button>
    </div>
  );
};
